import {
    Component,
    DestroyRef,
    Directive,
    ElementRef,
    HostListener,
    inject,
    Input,
    model,
    OnChanges,
    Renderer2,
    SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GuardsCheckStart, NavigationCancel, NavigationEnd, NavigationError, Router, RouterLink } from '@angular/router';


const coerceBoolean = (value: null | string | boolean) => value != null && `${value}` !== 'false' && value !== false;


/**
 * When there is a class .btn, set class .disabled when button is disabled
 * also unify type attribute across browsers - set it to noop (type="button")
 */
@Directive({
    selector: 'button.btn'
})
export class ButtonDirective {

    @Input()
    set type(value: string) {
        this.renderer.setAttribute(this.element.nativeElement, 'type', value);
    }

    @Input()
    set disabled(value: boolean | string) {
        const disabled = coerceBoolean(value);
        const el = this.element.nativeElement;
        el.disabled = disabled;
        if (disabled) {
            this.renderer.addClass(el, 'disabled');
        } else {
            this.renderer.removeClass(el, 'disabled');
        }
    }

    get disabled() {
        return this.element.nativeElement.disabled;
    }

    constructor(private element: ElementRef<HTMLButtonElement>, private renderer: Renderer2) {
        this.renderer.setAttribute(this.element.nativeElement, 'type', 'button');
    }
}


/**
 * when class .btn is applied to HTMLAnchorElement, add button behavior like disabled attribute and proper aria roles
 */
@Directive({
    selector: 'a.btn',
    host: {
        'attr.role': 'button',
    }
})
export class AnchorButtonDirective implements OnChanges {

    private _disabled: boolean;

    get disabled() {
        return this._disabled;
    }

    /**
     * keep same behavior as disabled in HTMLButtonElement - disabled without a value should be truthy
     */
    @Input() set disabled(value: string | boolean | null) {
        this._disabled = coerceBoolean(value);
    }

    constructor(private element: ElementRef<HTMLAnchorElement>, private renderer: Renderer2) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.disabled) {
            const el = this.element.nativeElement;
            if (this.disabled) {
                this.renderer.addClass(el, 'disabled');
                this.renderer.setProperty(el, 'tabIndex', -1);
                this.renderer.setAttribute(el, 'aria-disabled', 'true');
            } else {
                this.renderer.removeClass(el, 'disabled');
                this.renderer.setProperty(el, 'tabIndex', 0);
                this.renderer.setAttribute(el, 'aria-disabled', 'false');
            }
        }
    }

    @HostListener('click', ['$event'])
    onClick(event: Event) {
        // disabled link should not emit any click events
        if (this.disabled) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return false;
        }
    }

    @HostListener('focus', ['$event'])
    onFocus(event: Event) {
        // disabled link should not be selectable
        if (this.disabled) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return false;
        }
    }
}


/**
 * when .btn has loading binding set to true, show spin icon and keep button same width
 * applicable also on anchor button-likes
 */
@Component({
    selector: '.btn[loading], .dropdown-item[loading]',
    styles: [
        `
        :host {
            position: relative;
        }
        .loader {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            white-space: nowrap;
        }
        :host ::ng-deep .fa-circle-o-notch + .loading-text {
            margin-left: 0.35em;
        }
        `,
    ],
    host: {
        '[class.loading]': 'loading()',
    },
    template: `
        <span [style.visibility]="loading() ? 'hidden' : 'visible'">
            <ng-content></ng-content>
        </span>
        @if (loading()) {
            <span class="loader">
                <i class="fa fa-circle-o-notch fa-spin"></i>
                <ng-content select=".loading-text"></ng-content>
            </span>
        }
    `,
})
export class LoadingButtonComponent {

    loading = model<string | boolean>(false);
}


@Directive({
    selector: '.btn[loading][routerLink], .dropdown-item[loading][routerLink]'
})
export class ButtonLoadingRouteDirective {

    private button = inject(LoadingButtonComponent);

    private router = inject(Router);

    private routerLink = inject(RouterLink);

    private destroyRef = inject(DestroyRef);

    ngOnInit() {
        const url = this.routerLink.urlTree && this.router.serializeUrl(this.routerLink.urlTree);

        const endOfNavigation = (event: any): event is NavigationEnd =>
            event instanceof NavigationEnd || event instanceof NavigationError || event instanceof NavigationCancel;

        this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
            if (event instanceof GuardsCheckStart && url === event.url) {
                this.button.loading.set(true);
            } else if (endOfNavigation(event) && url === event.url) {
                this.button.loading.set(false);
            }
        });
    }
}
